import { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import {
  faArrowRotateLeft,
  faArrowsRotate,
} from "@fortawesome/pro-regular-svg-icons";
import { animate, motion } from "framer-motion";
import Typography from "core/components/Typography";
import Error from "core/components/Error";
import Button from "core/components/Button";
import ButtonLink from "core/components/ButtonLink";
import InfoFlash from "core/components/InfoFlash";
import Field from "core/components/Field";
import { Row } from "core/styles";
import { required, validate } from "common/validation";
import useProductLicenseForm from "core/hooks/useProductLicenseForm";
import useBundleFilters from "core/hooks/useBundleFilters";
import ReviewSummary from "modules/admin/OrgProductLicenseForm/DeploymentReview/ReviewSummary";
import {
  PRODUCT_BUNDLES,
  DEPLOYMENT_STATUSES,
} from "modules/admin/OrgProductLicenseForm/constants";

const ProductLicenseSummaryPage = () => {
  const {
    formState,
    licenseDeployment,
    licenseDeployRetryMutation,
    catalogError,
    productLicenseMutation,
    isLoadingLicenses,
    isLoadingCatalog,
    isLoadingSegments,
  } = useProductLicenseForm();

  const {
    getDynamicProduct,
    getProductIdsEnabled,
    productsEnabled,
    productIdsEnabled,
    getPreparedLicenses,
    clearAuditTrail,
  } = formState;

  const { selectedBundleIds, bundleOptionValues, updateBundleOptions } =
    useBundleFilters();

  const resetScroll = useCallback(() => {
    animate(window.scrollY, 0, {
      duration: 0.5,
      onUpdate: (scrollY) => window.scrollTo(window.scrollX, scrollY),
    });
  }, []);

  const isFormLoading =
    isLoadingLicenses || isLoadingCatalog || isLoadingSegments;

  const error = catalogError || productLicenseMutation.error;

  const configuredBundles = useMemo(() => {
    return productsEnabled
      .reduce((bundleIds, product) => {
        const productBundleId = product.meta?.bundle?.id;

        if (bundleIds.includes(productBundleId)) {
          return bundleIds;
        }

        return bundleIds.concat(productBundleId);
      }, [])
      .map((bundleId) => PRODUCT_BUNDLES[bundleId]);
  }, [productsEnabled]);

  return (
    <motion.div
      initial={{
        opacity: 1,
        y: 0,
      }}
      exit={{
        opacity: 0,
        y: 20,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      transition={{
        opacity: {
          duration: 0.4,
        },
        y: {
          duration: 0.3,
        },
      }}
      style={{
        width: "100%",
        paddingBottom: "100px",
      }}
    >
      <Error style={{ marginBottom: "10px" }}>{error}</Error>

      {licenseDeployment?.status === DEPLOYMENT_STATUSES.QUEUED && (
        <InfoFlash
          style={{
            backgroundColor: "#dcdcdc",
            color: "#000000",
            fontWeight: "400",
            marginBottom: "10px",
          }}
        >
          This page is restricted to read-only while a deployment is queued.
        </InfoFlash>
      )}

      {licenseDeployment?.status === DEPLOYMENT_STATUSES.IN_PROGRESS && (
        <InfoFlash style={{ marginBottom: "10px" }}>
          This page is restricted to read-only while a deployment is in
          progress.
        </InfoFlash>
      )}

      {getProductIdsEnabled().length === 0 && (
        <InfoFlash
          style={{
            backgroundColor: "#ffffff",
            color: "#000000",
            fontWeight: "600",
            marginBottom: "10px",
            border: "1px solid #979797",
          }}
        >
          No products are enabled in your current license deployment.
        </InfoFlash>
      )}

      {[DEPLOYMENT_STATUSES.DEFAULT].includes(licenseDeployment?.status) ? (
        <div>
          <div>
            <Typography variant="p" color="black">
              Before you configure your product license, please select which
              bundles you want to build your license from.
            </Typography>
            <div>
              <Field
                id="provider-product-bundles"
                type="checkbox"
                labelText="Provider Products Bundles"
                validator={validate(required())}
                value={bundleOptionValues}
                grouped={true}
                onChange={updateBundleOptions}
                isRequired={true}
                style={{
                  iconBackgroundColor: "#541299",
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <ReviewSummary
          bundles={PRODUCT_BUNDLES}
          configuredBundles={configuredBundles}
          products={productsEnabled}
          productsEnabled={productIdsEnabled}
          getDynamicProduct={getDynamicProduct}
          isLoading={isFormLoading}
          deploymentStatus={licenseDeployment?.status}
          isDirty={false}
        />
      )}

      <Row style={{ justifyContent: "flex-end", gap: "20px" }}>
        {!licenseDeployment ||
        [
          DEPLOYMENT_STATUSES.DEFAULT,
          DEPLOYMENT_STATUSES.IN_PROGRESS,
          DEPLOYMENT_STATUSES.QUEUED,
          DEPLOYMENT_STATUSES.SUCCESS,
        ].includes(licenseDeployment?.status) ? null : (
          <Button
            type="button"
            variant="outlined"
            onClick={licenseDeployRetryMutation.mutate}
            isLoading={licenseDeployRetryMutation.isPending}
            width="fit-content"
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <FontAwesomeIcon icon={faArrowsRotate} />
              <span>Retry Deployment</span>
            </span>
          </Button>
        )}

        {!!getPreparedLicenses() && (
          <Button
            type="button"
            variant="outlined"
            onClick={clearAuditTrail}
            width="fit-content"
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <FontAwesomeIcon icon={faArrowRotateLeft} />
              <span>Reset</span>
            </span>
          </Button>
        )}

        {[
          DEPLOYMENT_STATUSES.DEFAULT,
          DEPLOYMENT_STATUSES.SUCCESS,
          DEPLOYMENT_STATUSES.FAILED,
          DEPLOYMENT_STATUSES.TIMEOUT,
        ].includes(licenseDeployment?.status) && (
          <ButtonLink
            to="../configure"
            onClick={() => {
              resetScroll();
            }}
            disabled={
              licenseDeployment?.status === DEPLOYMENT_STATUSES.DEFAULT &&
              selectedBundleIds?.length === 0
            }
            data-test="configure-button"
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {[
                DEPLOYMENT_STATUSES.FAILED,
                DEPLOYMENT_STATUSES.SUCCESS,
                DEPLOYMENT_STATUSES.TIMEOUT,
              ].includes(licenseDeployment?.status)
                ? "Modify License"
                : "Configure License"}

              <FontAwesomeIcon
                icon={faArrowRight}
                size="sm"
                style={{ marginBottom: "-1px" }}
              />
            </div>
          </ButtonLink>
        )}
      </Row>
    </motion.div>
  );
};

export default ProductLicenseSummaryPage;
